/**
 * Switch — av/på-bryter (#152, mangler i MAPPING.md §3).
 *
 * `role="switch"` med `aria-checked`. Sporet er 52 × 32, knotten 26 px og
 * hvit i begge temaer (Hendelse- og Meg-skissene). På er lime (`bg-fab`, likt
 * i begge temaer); av er `bg-secondary-line`, samme kantfarge som
 * sekundærknappene, så sporet synes også på et hvitt ark i lyst tema.
 * Knottens plass (høyre/venstre) er kjennetegnet i tillegg til fargen.
 *
 * Navnet kommer enten fra `label` (aria-label) eller fra et synlig element
 * via `aria-labelledby`. Bryteren selv er 44 px høy som trykkflate; sporet
 * er sentrert i den.
 *
 * Under redusert bevegelse nuller motion.css overgangen, så knotten hopper.
 */
import type { ReactNode } from 'react';

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Tilgjengelig navn når det ikke finnes en synlig etikett. */
  label?: string;
  /** id-en til den synlige etiketten. */
  labelledBy?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  labelledBy,
  disabled = false,
  className,
  'data-testid': testId,
}: SwitchProps): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-labelledby={labelledBy}
      disabled={disabled}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={[
        'inline-flex h-11 w-13 shrink-0 items-center',
        disabled ? 'opacity-40 pointer-events-none' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        aria-hidden
        className={[
          'flex h-8 w-13 items-center rounded-pill p-0.75 transition-colors',
          checked ? 'justify-end bg-fab' : 'justify-start bg-secondary-line',
        ].join(' ')}
      >
        <span className="block h-6.5 w-6.5 rounded-full bg-knob shadow-knob" />
      </span>
    </button>
  );
}
